import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Archive, ChevronRight, ChevronLeft, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Language } from '../App';
interface ArchivePreviewProps {
  language: Language;
}
export function ArchivePreview({
  language
}: ArchivePreviewProps) {
  const isPashto = language === 'pashto';
  const archiveYears = [{
    year: 2016,
    yearPs: '۲۰۱۶',
    vol: 11,
    volPs: '۱۱',
    issues: 3,
    issuesPs: '۳'
  }, {
    year: 2015,
    yearPs: '۲۰۱۵',
    vol: 10,
    volPs: '۱۰',
    issues: 6,
    issuesPs: '۶'
  }, {
    year: 2014,
    yearPs: '۲۰۱۴',
    vol: 9,
    volPs: '۹',
    issues: 6,
    issuesPs: '۶'
  }, {
    year: 2013,
    yearPs: '۲۰۱۳',
    vol: 8,
    volPs: '۸',
    issues: 6,
    issuesPs: '۶'
  }, {
    year: 2012,
    yearPs: '۲۰۱۲',
    vol: 7,
    volPs: '۷',
    issues: 6,
    issuesPs: '۶'
  }, {
    year: 2011,
    yearPs: '۲۰۱۱',
    vol: 6,
    volPs: '۶',
    issues: 6,
    issuesPs: '۶'
  }];
  return <section className="py-10 md:py-14 bg-neutral-50 px-6" data-id="element-44">
      <div className="max-w-5xl mx-auto" data-id="element-45">
        <AnimatePresence mode="wait" data-id="element-46">
          <motion.div key={language} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.4
        }} data-id="element-47">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 text-start" data-id="element-48">
              <div data-id="element-49">
                <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-dark inline-block relative ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-50">
                  {isPashto ? 'آرشیف وپلټئ' : 'Explore the Archive'}
                  <span className="absolute -bottom-3 start-0 w-1/2 h-1 bg-pasoon-red rounded-full" data-id="element-51"></span>
                </h2>
                <p className={`text-gray-500 text-base md:text-lg mt-5 ${isPashto ? 'font-pashto' : ''}`} data-id="element-52">
                  {isPashto ? 'د پښتو ادب له یوې لسیزې ډیر — د ۲۰۰۵ دسمبر تر اوسه' : 'Over a decade of Pashto literature — December 2005 to present'}
                </p>
              </div>
              <Link to="/archive" className={`hidden sm:flex items-center gap-1.5 text-pasoon-red font-medium hover:text-red-800 transition-colors group whitespace-nowrap ${isPashto ? 'font-pashto' : ''}`} data-id="element-53">
                {isPashto ? 'بشپړ آرشیف وګورئ' : 'View Full Archive'}
                {isPashto ? <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" data-id="element-54" /> : <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-55" />}
              </Link>
            </div>

            {/* Year Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-56">
              {archiveYears.map((item, idx) => <motion.div key={item.year} initial={{
              opacity: 0,
              y: 15
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: idx * 0.06,
              duration: 0.4
            }} data-id="element-57">
                  <Link to="/archive" className="block bg-white rounded-xl border border-gray-200 p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all group" data-id="element-58">
                    <div className="flex items-start justify-between mb-4" data-id="element-59">
                      <div className="flex items-center gap-3" data-id="element-60">
                        <div className="w-11 h-11 rounded-xl bg-pasoon-red/10 flex items-center justify-center flex-shrink-0" data-id="element-61">
                          <Archive className="w-5 h-5 text-pasoon-red" data-id="element-62" />
                        </div>
                        <div data-id="element-63">
                          <h3 className={`text-2xl font-bold text-pasoon-dark ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-64">
                            {isPashto ? item.yearPs : item.year}
                          </h3>
                          <p className={`text-gray-400 text-xs ${isPashto ? 'font-pashto' : ''}`} data-id="element-65">
                            {isPashto ? `ټوک ${item.volPs} — ${item.issuesPs} ګڼې` : `Vol ${item.vol} — ${item.issues} Issues`}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className={`flex items-center gap-1 text-pasoon-red text-sm font-medium group-hover:text-red-800 transition-colors ${isPashto ? 'font-pashto' : ''}`} data-id="element-66">
                      <BookOpen className="w-4 h-4" data-id="element-67" />
                      {isPashto ? 'آرشیف وګورئ' : 'View Archive'}
                      {isPashto ? <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1" data-id="element-68" /> : <ChevronRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" data-id="element-69" />}
                    </div>
                  </Link>
                </motion.div>)}
            </div>

            {/* Mobile CTA */}
            <div className="mt-10 sm:hidden" data-id="element-70">
              <Link to="/archive" className={`w-full flex items-center justify-center gap-2 px-8 py-4 bg-pasoon-red text-white rounded-full font-medium text-base hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-71">
                <Archive className="w-5 h-5" data-id="element-72" />
                {isPashto ? 'بشپړ آرشیف وګورئ' : 'View Full Archive'}
              </Link>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}